import { NetFetch } from '../Net/NetFetch';
import { UnauthorizedError } from './Error/UnauthorizedError';
import { StatusCodes } from './Status/StatusCodes';
/**
 * SightingMovement API helper.
 *
 * Backend lazily computes the movement on sighting save / mobile tracking
 * sync, so `getList` returns whatever has been computed so far. Sightings
 * whose movement hasn't been computed yet are silently omitted.
 */
export class SightingMovement {
    /**
     * Bulk-fetch movements for a set of sighting ids. Returns an empty
     * array on any non-OK response. UNAUTHORIZED still throws so the
     * page can redirect to login.
     */
    static async getList(sightingIds) {
        var _a;
        if (sightingIds.length === 0) {
            return [];
        }
        const req = { sighting_ids: sightingIds };
        const result = await NetFetch.postData('/json/sighting/movement/list', req);
        if (result && result.statusCode) {
            switch (result.statusCode) {
                case StatusCodes.OK:
                    return (_a = (result.list)) !== null && _a !== void 0 ? _a : [];
                case StatusCodes.UNAUTHORIZED:
                    throw new UnauthorizedError();
            }
        }
        return [];
    }
    /**
     * Trigger the admin bulk-rebuild. Synchronous on the server side —
     * the promise resolves only when every sighting has been processed.
     * Returns the server's free-form status message (counts).
     */
    static async rebuildAll() {
        var _a;
        const result = await NetFetch.getData('/json/sighting/movement/rebuild_all');
        if (result && result.statusCode) {
            switch (result.statusCode) {
                case StatusCodes.OK:
                    return (_a = result.msg) !== null && _a !== void 0 ? _a : '';
                case StatusCodes.UNAUTHORIZED:
                    throw new UnauthorizedError();
                case StatusCodes.FORBIDDEN:
                    throw new Error('Forbidden — admin role required.');
                default:
                    if (result.msg) {
                        throw new Error(result.msg);
                    }
            }
        }
        throw new Error('Rebuild failed (no response).');
    }
    /**
     * Read the current MovementConfig (admin only). Throws on auth
     * problems or missing payload so the modal can show a banner;
     * otherwise returns the effective values.
     */
    static async getConfig() {
        const result = await NetFetch.getData('/json/sighting/movement/config');
        if (result && result.statusCode) {
            switch (result.statusCode) {
                case StatusCodes.OK:
                    if (result.config) {
                        return result.config;
                    }
                    throw new Error('Config response missing payload.');
                case StatusCodes.UNAUTHORIZED:
                    throw new UnauthorizedError();
                case StatusCodes.FORBIDDEN:
                    throw new Error('Forbidden — admin role required.');
                default:
                    if (result.msg) {
                        throw new Error(result.msg);
                    }
            }
        }
        throw new Error('Config read failed (no response).');
    }
    /**
     * Persist a new MovementConfig (admin only). Backend validates each
     * field and returns the saved values; any validation problem is
     * thrown as an Error with the backend's `msg` so the modal can
     * surface it directly.
     */
    static async saveConfig(config) {
        const result = await NetFetch.postData('/json/sighting/movement/config', config);
        if (result && result.statusCode) {
            switch (result.statusCode) {
                case StatusCodes.OK:
                    if (result.config) {
                        return result.config;
                    }
                    throw new Error('Config response missing payload.');
                case StatusCodes.UNAUTHORIZED:
                    throw new UnauthorizedError();
                case StatusCodes.FORBIDDEN:
                    throw new Error('Forbidden — admin role required.');
                default:
                    if (result.msg) {
                        throw new Error(result.msg);
                    }
            }
        }
        throw new Error('Config save failed (no response).');
    }
}
